// Product details accordion behaviour
// Scoped to the PDP product-details column: makes the collapsible tabs behave
// as an exclusive accordion (only one row open at a time). The theme already
// animates a row's own open/close via base.css (`accordion-custom details`),
// but that animation is gated behind `:focus-within`, so a sibling we close
// programmatically would otherwise snap shut. We tag the closing row so a
// scoped CSS rule can re-enable the transition for that one collapse, keeping
// the swap smooth.
//
// Note: we deliberately do NOT use the native `<details name>` grouping. The
// browser would close the previously-open row synchronously (with no
// animation) before our `toggle` handler runs, defeating the smooth close.
// Driving the collapse ourselves keeps it animated.
const CLOSING_ATTR = 'data-exclusive-closing';
// Matches --animation-speed-slow (0.2s) plus a small buffer.
const CLOSE_ANIMATION_MS = 350;

class ProductDetailsAccordion extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    // Delegated so rows added/removed in the theme editor are covered without
    // re-binding. `toggle` doesn't bubble, so listen in the capture phase.
    this.addEventListener('toggle', this.#handleToggle, {
      capture: true,
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Direct accordion rows only — never a `<details>` nested inside an app or
   * theme block placed within a row's content.
   * @returns {HTMLDetailsElement[]}
   */
  #rows() {
    return /** @type {HTMLDetailsElement[]} */ (
      Array.from(this.querySelectorAll('.accordion > accordion-custom > details'))
    );
  }

  /**
   * When a row opens, collapse the others — animating the one being closed.
   * @param {Event} event
   */
  #handleToggle = (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;

    const rows = this.#rows();
    if (!rows.includes(opened)) return; // ignore nested <details>

    for (const details of rows) {
      if (details === opened || !details.open) continue;
      this.#closeRow(details);
    }
  };

  /**
   * Closes a row with the collapse animation enabled.
   * @param {HTMLDetailsElement} details
   */
  #closeRow(details) {
    const row = details.parentElement; // <accordion-custom>
    if (!row) {
      details.open = false;
      return;
    }

    row.setAttribute(CLOSING_ATTR, '');
    details.open = false;

    // Remove the flag once the collapse has finished so normal (snap) behaviour
    // resumes for programmatic toggles like open-by-default on resize.
    window.setTimeout(() => row.removeAttribute(CLOSING_ATTR), CLOSE_ANIMATION_MS);
  }
}

if (!customElements.get('product-details-accordion')) {
  customElements.define('product-details-accordion', ProductDetailsAccordion);
}
